<script setup>
import {
  ref, computed, onMounted, watch,
} from 'vue';
import api from '@shop/frontend/web/js/api';
import { useCartDataProvider } from '@vueApp/hooks/useCartDataProvider.js';
import useCartStore from '@vueApp/store/modules/cart';
import { translator } from '@shop/frontend/web/js/services/TranslatorService';
import { utils } from '@frontend/web/src/js/utils';
import { Tooltip } from 'bootstrap';

const cartStore = useCartStore();
const { isGuest, isFastBuy } = useCartDataProvider();

const props = defineProps({
  url: {
    type: String,
    default() {
      return '';
    },
  },
  options: {
    type: Object,
    default() {
      return {};
    },
  },
  smallBtn: {
    type: Boolean,
    default: false,
  },
  ticketsCount: {
    type: Number,
    default: 0,
  },
  firstEventId: {
    type: Number,
    default: null,
  },
  eventType: {
    type: String,
    required: true,
  },
  isDisable: {
    type: Boolean,
    default: false,
  },
  tooltipText: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['setAcquiringError']);

const isAcquiringPushkinOrCinema = ref(
  props.options.payType === 'Pushkin' || props.options.payType === 'Cinema',
);

const formatEventType = computed(() => (props.eventType === 'service_simple' ? 'service' : 'event'));

const acquiringButtonClass = computed(() => ({
  'js-get-fast-registration': isGuest,
  'i-acquirings-button_small': props.smallBtn,
  'i-acquirings-button_disabled': (props.ticketsCount > 1 && isAcquiringPushkinOrCinema.value) || props.isDisable,
}));

function changePayAcquiring(acquiringId) {
  api.pay
    .changePayAcquiring({ orderId: props.options.orderId, acquiringId })
    .then((res) => {
      if (isGuest) {
        return;
      }

      return (window.location.href = props.url);
    })
    .catch((err) => {
      emit('setAcquiringError', {
        isAcquiringError: true,
        errorMessage: err.message ?? '',
      });
    });
}

function payForOrder(event) {
  if (props.isDisable) {
    event.preventDefault();
    event.stopPropagation();
    return;
  }

  const acquiringId = props.options.id;

  if (acquiringId !== 0) {
    event.preventDefault();
    changePayAcquiring(acquiringId);
    return;
  }

  if (isGuest) {
    event.preventDefault();
    cartStore.setCurrentOpenedEventId(props.firstEventId);
    cartStore.setFastBuyModalStatus(true);
    cartStore.setEventTypeToPay(formatEventType.value);
  }
}

const acquiringButtonRef = ref();
const tooltipInstance = ref();

function disposeTooltip() {
  if (tooltipInstance.value) {
    tooltipInstance.value.dispose();
    tooltipInstance.value = null;
  }
}

function updateTooltip(isDisabled, tooltipText) {
  if (!acquiringButtonRef.value) return;

  if (isDisabled && tooltipText) {
    disposeTooltip();
    tooltipInstance.value = new Tooltip(acquiringButtonRef.value, {
      title: tooltipText,
      trigger: 'hover focus',
      placement: 'top',
      customClass: 'i-acquirings-button__tooltip',
    });
  } else {
    disposeTooltip();
  }
}

onMounted(() => {
  if (acquiringButtonRef.value && props.isDisable) {
    tooltipInstance.value = new Tooltip(acquiringButtonRef.value, {
      title: props.tooltipText,
      trigger: 'hover focus',
      placement: 'top',
      customClass: 'i-acquirings-button__tooltip',
    });
  }
});

watch(
  () => props.isDisable,
  (newValue) => {
    updateTooltip(newValue, props.tooltipText);
  },
);

watch(
  () => props.tooltipText,
  (newValue) => {
    updateTooltip(props.isDisable, newValue);
  },
);
</script>

<template>
  <button
    v-if="isGuest && !isFastBuy"
    class="btn i-acquirings-button"
    data-bs-toggle="modal"
    data-bs-target="#loginModal"
  >
    <slot>
      {{ translator.t('main', 'Pay') }}
    </slot>
  </button>
  <div v-else class="i-acquirings-button__link-wrapper">
    <a
      ref="acquiringButtonRef"
      :href="url"
      class="btn i-acquirings-button"
      :class="acquiringButtonClass"
      :title="tooltipMessage || ''"
      :data-bs-toggle="''"
      data-bs-placement="top"
      @click="payForOrder($event)"
    >
      <slot>
        <svg class="icon i-acquirings-button__icon">
          <use :xlink:href="`${isAcquiringPushkinOrCinema ? '#pushkin' : '#card'}`" />
        </svg>
        <template v-if="!options.name"> {{ translator.t('main', 'Pay') }} {{ utils.formatNumber(options.sum) }} <span class="ruble">₽</span></template>
        <template v-else> {{ options.name }}</template>
      </slot>
    </a>
  </div>
</template>

<style lang="scss">
@import "frontend/web/src/styles/utils/variables.scss";
@import "./i-acquirings-button.scss";

:deep(.popper) {
  width: 280px;
  padding: 5px;

  border-radius: 6px;
  box-shadow: 0px 5px 5px rgba(0, 0, 0, 0.05);
  background-color: $white;

  color: $black;
  text-align: center;
  font-size: 14px;
  font-weight: 400;
}

:deep(.popper #arrow::before) {
  transform: translateX(-5px) rotate(45deg);
  background: $white;
}

.i-acquirings-button__tooltip.tooltip .tooltip-inner {
  background-color: $grey-background;
  color: $white;
  font-size: 14px;
  border-radius: 4px;
  padding: 12px;
  max-width: 450px;
  text-align: center;
}
.i-acquirings-button__tooltip.tooltip .tooltip-arrow::before,
.i-acquirings-button__tooltip.tooltip .tooltip-arrow::after {
  border-top-color: $grey-background;
}
</style>
